import React, { useState, useEffect, useRef } from 'react';
import { onUpdate, skipWaiting } from './registerSW';
import { useTranslation } from '../hooks/useTranslation';
import './UpdateNotification.css';

// Standard service-worker update flow:
//  1. a new worker installs and waits (sw.js never calls skipWaiting itself)
//  2. this banner appears, non-blocking, and the user keeps working
//  3. only when the user clicks "Refresh" we post SKIP_WAITING and reload once
//     the new worker has taken control (controllerchange).
// First-time installs never produce a controllerchange we act on, and nothing
// ever reloads unless the user asked for it.
const UpdateNotification = () => {
  const { t, language } = useTranslation();
  const [show, setShow] = useState(false);
  const [updating, setUpdating] = useState(false);
  const userRequestedRef = useRef(false);

  useEffect(() => {
    onUpdate(() => setShow(true));

    if (!('serviceWorker' in navigator)) return undefined;

    let reloaded = false;
    const handleControllerChange = () => {
      if (!userRequestedRef.current || reloaded) return;
      reloaded = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  if (!show) return null;

  const handleUpdate = () => {
    userRequestedRef.current = true;
    setUpdating(true);
    skipWaiting();
    // Safety net: if the waiting worker is gone (e.g. already activated in
    // another tab) no controllerchange arrives; the user asked for a refresh.
    setTimeout(() => {
      if (userRequestedRef.current) window.location.reload();
    }, 4000);
  };

  return (
    <div className="pwa-update-bar" role="status" aria-live="polite" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="pwa-update-icon-wrap" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 2v6h-6" />
          <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
          <path d="M3 22v-6h6" />
          <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
        </svg>
      </div>
      <div className="pwa-update-text">
        <strong className="pwa-update-title">{t.updateAvailable}</strong>
        <span className="pwa-update-desc">{t.updateAvailableDesc}</span>
      </div>
      <div className="pwa-update-actions">
        <button type="button" className="pwa-update-btn-primary" onClick={handleUpdate} disabled={updating}>
          {updating ? t.updating : t.refresh}
        </button>
        <button type="button" className="pwa-update-btn-dismiss" onClick={() => setShow(false)} aria-label={t.later}>
          {t.later}
        </button>
      </div>
    </div>
  );
};

export default UpdateNotification;
